import { noop } from '@tanstack/react-query'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  homeMoversQueryOptions,
  homePortfolioSummaryQueryOptions,
} from '@/features/home/api/queries'
import { indexSummaryQueryOptions, marketMoodQueryOptions } from '@/features/market/api/queries'
import { valueHistoryQueryOptions } from '@/features/portfolio/api/queries'
import { watchlistCollectionQueryOptions } from '@/features/watchlist/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { HOME_INDEX_DEFAULT_RANGE, HOME_VALUE_FIRST_PERIOD } from '@/screens/app/home/lib/homeReads'

/**
 * The `/` loader.
 *
 * MAIN, awaited: the reads that decide the page's SHAPE rather than what one
 * card says — which blocks exist and how tall they are. The rest of Home is a
 * column of fixed-size cards, each of which can hold its own box with a
 * skeleton, but these three cannot be skeletoned honestly because the answer
 * changes the layout itself:
 *
 *   portfolio summary   which card fills the portfolio slot: "Your
 *                       portfolio" (~270px with its book strip), or "Track
 *                       your portfolio here" for a member with no holdings.
 *   watchlist           whether its slot is the "Nothing followed yet"
 *                       card, a failure card, or nothing.
 *   the value curve     how tall the portfolio card is: the curve, one day's
 *                       reading, or today's usual "tracked from today" line
 *                       are three different heights. Only waited on when the
 *                       summary says there is a book to draw it in.
 *
 * Awaited, but never thrown. Each of these already has a slot-sized answer
 * for a failed read — the watchlist a "did not load" card, the portfolio
 * nothing (a member with no book must not be told it failed), the curve an
 * inline note — so a failure here settles the loader like a success would
 * and the page draws that answer on its first frame. Blanking the whole of
 * Home, the page the shell lands on, because one card's read failed would be
 * a worse page than the one it replaced.
 *
 * SECONDARY, started first and not awaited, on a real navigation only:
 * NEPSE today's opening range and its mood cell (the Fear & Greed reading off
 * `/market/summary/`'s `fear_greed`, the same request the range's own read makes,
 * through the endpoint cache), both sides of Gainers and losers, and the value curve
 * (started alongside the summary rather than one round trip after it; for a
 * member with no book it is a cheap empty answer nothing reads). A hover
 * preload loads only what decides the shape — the value curve then waits for
 * the summary — so a cursor crossing a link to Home does not fire the cards'
 * reads too. The Brief reads its own `useIndexSummary('live')`, a cache entry
 * this loader does not start — that range resolves to the same wire request
 * as NEPSE today's opening `'1w'` (`marketService.ts`'s own `RANGE_BY_UI`), so
 * the Brief's read still lands from a warm HTTP cache even though its own
 * React Query key is cold. Below the fold — Today's news (an infinite query),
 * Company events and the issues table — each card starts its own read on
 * mount, behind a skeleton of its loaded size.
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own. It imports from `api/` and `lib/` modules only — route loaders
 * are not code-split, so anything imported here ships in the entry chunk.
 *
 * `retry: false` on all three awaited-but-swallowed reads below, on top of
 * their own `.catch`: the app's default policy still retries a 5xx twice (1s,
 * then 2s) BEFORE that catch ever runs, and longer in a background tab, since
 * React Query pauses retries while hidden — so a single failed read held this
 * page's own skeleton up for 3s or more it did not need to wait through, for
 * data it was about to treat as absent either way. Nothing loses the retry:
 * `HomeScreen`'s own hooks (`usePortfolioSummary`/`useWatchlistCollection`/
 * `useValueHistory`, all under the app's normal `queryClient`) mount on the
 * same cache entries and retry them again from there — this only shortens
 * what the LOADER waits through before handing the page to the screen.
 */
export async function loadHome({
  context: { queryClient },
  preload,
}: {
  context: RouterContext
  preload: boolean
}): Promise<void> {
  if (!preload) {
    prefetchRouteData(queryClient, indexSummaryQueryOptions(HOME_INDEX_DEFAULT_RANGE))
    prefetchRouteData(queryClient, marketMoodQueryOptions())
    prefetchRouteData(queryClient, homeMoversQueryOptions('gainers'))
    prefetchRouteData(queryClient, homeMoversQueryOptions('losers'))
    prefetchRouteData(queryClient, valueHistoryQueryOptions(HOME_VALUE_FIRST_PERIOD))
  }

  const [summary] = await Promise.all([
    ensureRouteData(queryClient, {
      ...homePortfolioSummaryQueryOptions(),
      retry: false,
    }).catch(() => null),
    ensureRouteData(queryClient, {
      ...watchlistCollectionQueryOptions(),
      retry: false,
    }).catch(noop),
  ])
  if (summary !== null && summary.holdingsCount > 0) {
    await ensureRouteData(queryClient, {
      ...valueHistoryQueryOptions(HOME_VALUE_FIRST_PERIOD),
      retry: false,
    }).catch(noop)
  }
}
